import React from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Rocket, Gift, Check } from "lucide-react";
import { Button } from "antd";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

const FeatureSection = () => {
  const { language } = useLanguage();
  const t = content[language].feature; // Lade die Übersetzungen für den Feature-Bereich

  // Animationen und Refs für jede Card
  const controls1 = useAnimation();
  const controls2 = useAnimation();
  const controls3 = useAnimation();

  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.2 });

  React.useEffect(() => {
    if (inView1) controls1.start("visible");
    if (inView2) controls2.start("visible");
    if (inView3) controls3.start("visible");
  }, [inView1, inView2, inView3, controls1, controls2, controls3]);

  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, delay } },
  });

  return (
    <div className="w-[100%] px-8 md:px-12 md:py-12 mb-12">
      <div className="grid gap-12 lg:grid-cols-[30%_30%_30%] justify-center">
        {/* Erste Card */}
        <motion.div
          ref={ref1}
          variants={fadeInUp(0.2)}
          initial="hidden"
          animate={controls1}
          className="bg-[#f0f0f0] rounded-2xl p-8 text-gray-800 shadow-md relative md:w-[100%]"
          style={{
            clipPath:
              "polygon( 15.046% 11.482%,15.046% 11.482%,14.034% 11.575%,13.074% 11.844%,12.179% 12.275%,11.361% 12.852%,10.635% 13.561%,10.011% 14.388%,9.504% 15.318%,9.125% 16.337%,8.889% 17.429%,8.807% 18.58%,8.807% 83.925%,8.807% 83.925%,8.872% 84.839%,9.06% 85.707%,9.36% 86.515%,9.763% 87.254%,10.258% 87.911%,10.836% 88.474%,11.485% 88.932%,12.196% 89.274%,12.958% 89.488%,13.761% 89.562%,89.541% 89.562%,89.541% 89.562%,90.345% 89.488%,91.107% 89.274%,91.818% 88.932%,92.467% 88.474%,93.044% 87.911%,93.54% 87.254%,93.942% 86.515%,94.243% 85.707%,94.431% 84.839%,94.495% 83.925%,94.495% 11.482%,94.495% 11.482%,94.431% 10.568%,94.243% 9.701%,93.942% 8.892%,93.54% 8.153%,93.044% 7.496%,92.467% 6.933%,91.818% 6.475%,91.107% 6.133%,90.345% 5.919%,89.541% 5.846%,55.596% 5.846%,55.596% 5.846%,54.577% 5.963%,53.582% 6.288%,52.607% 6.778%,51.647% 7.389%,50.698% 8.078%,49.753% 8.804%,48.808% 9.523%,47.858% 10.193%,46.898% 10.77%,45.923% 11.212%,45.923% 11.212%,45.778% 11.262%,45.632% 11.308%,45.484% 11.348%,45.334% 11.383%,45.183% 11.413%,45.03% 11.438%,44.875% 11.457%,44.719% 11.471%,44.562% 11.479%,44.404% 11.482%,15.046% 11.482% )",
          }}
        >
          <div className="space-y-4 p-8 flex flex-col gap-4 mt-4">
            <div className="inline-block rounded-lg bg-[#FF7051]/10 p-3 absolute top-8 right-12">
              <Rocket className="w-6 h-6 text-[#FF7051]" />
            </div>
            <h3 className="md:text-lg lg:text-xl font-bold">{t.card1.title}</h3>
            <p className="text-gray-500 text-sm">{t.card1.description}</p>
          </div>
        </motion.div>

        {/* Zweite Card */}
        <motion.div
          ref={ref2}
          variants={fadeInUp(0.4)}
          initial="hidden"
          animate={controls2}
          className="bg-[#FF7051] rounded-2xl p-8 text-white shadow-md relative"
          style={{
            clipPath:
              "polygon( 15.046% 11.482%,15.046% 11.482%,14.034% 11.575%,13.074% 11.844%,12.179% 12.275%,11.361% 12.852%,10.635% 13.561%,10.011% 14.388%,9.504% 15.318%,9.125% 16.337%,8.889% 17.429%,8.807% 18.58%,8.807% 83.925%,8.807% 83.925%,8.872% 84.839%,9.06% 85.707%,9.36% 86.515%,9.763% 87.254%,10.258% 87.911%,10.836% 88.474%,11.485% 88.932%,12.196% 89.274%,12.958% 89.488%,13.761% 89.562%,89.541% 89.562%,89.541% 89.562%,90.345% 89.488%,91.107% 89.274%,91.818% 88.932%,92.467% 88.474%,93.044% 87.911%,93.54% 87.254%,93.942% 86.515%,94.243% 85.707%,94.431% 84.839%,94.495% 83.925%,94.495% 11.482%,94.495% 11.482%,94.431% 10.568%,94.243% 9.701%,93.942% 8.892%,93.54% 8.153%,93.044% 7.496%,92.467% 6.933%,91.818% 6.475%,91.107% 6.133%,90.345% 5.919%,89.541% 5.846%,55.596% 5.846%,55.596% 5.846%,54.577% 5.963%,53.582% 6.288%,52.607% 6.778%,51.647% 7.389%,50.698% 8.078%,49.753% 8.804%,48.808% 9.523%,47.858% 10.193%,46.898% 10.77%,45.923% 11.212%,45.923% 11.212%,45.778% 11.262%,45.632% 11.308%,45.484% 11.348%,45.334% 11.383%,45.183% 11.413%,45.03% 11.438%,44.875% 11.457%,44.719% 11.471%,44.562% 11.479%,44.404% 11.482%,15.046% 11.482% )",
          }}
        >
          <div className="space-y-4 p-8 flex flex-col gap-4 mt-4">
            <div className="inline-block rounded-lg bg-white/10 p-3 absolute top-8 right-12">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <h3 className="md:text-lg lg:text-xl font-bold">{t.card2.title}</h3>
            <p className="text-white/90 text-sm">{t.card2.description}</p>
          </div>
        </motion.div>

        {/* Dritte Card */}
        <motion.div
          ref={ref3}
          variants={fadeInUp(0.6)}
          initial="hidden"
          animate={controls3}
          className="flex flex-col gap-4 py-6 h-full px-8"
        >
          <div className="text-center">
            <h2 className="text-lg font-bold mb-4">{t.card3.title}</h2>
            <ul className="inline-block text-left space-y-3 text-sm">
              {t.card3.bulletPoints.map((point, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-[#FF7051]" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <Button className="bg-[#232F4E] text-white hover:bg-[#232F5E] hover:opacity-100">
            <a href="#How-it-works" className="text-sm font-medium">
              {t.card3.button}
            </a>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default FeatureSection;
